import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api/client';
import { queryKeys } from '@/lib/query/query-keys';
import type { TripDetail, TripDetailCity } from '@/hooks/trips/types';

interface ToggleTransportationBookingInput {
  tripId: string;
  cityId: number;
  transportationId: number;
  isBooked: boolean;
  amount?: number;
}

async function toggleTransportationBooking(
  transportationId: number,
  isBooked: boolean,
  amount?: number
): Promise<void> {
  const response = await apiFetch(
    `/api/v1/business-trip-transportations/${transportationId}/book`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        is_booked: isBooked,
        ...(typeof amount === 'number' ? { amount } : {}),
      }),
    }
  );

  if (!response.ok) {
    const payload = await response.json().catch(() => null);
    const message =
      payload && typeof payload === 'object' && 'error' in (payload as Record<string, unknown>)
        ? String(
            (payload as Record<string, { message?: string }>).error?.message ??
              'Failed to update transport booking status'
          )
        : 'Failed to update transport booking status';

    throw new Error(message);
  }
}

export function useToggleTransportationBooking() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ transportationId, isBooked, amount }: ToggleTransportationBookingInput) =>
      toggleTransportationBooking(transportationId, isBooked, amount),
    onMutate: async ({ tripId, cityId, transportationId, isBooked, amount }) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.trip(tripId) });
      await queryClient.cancelQueries({ queryKey: queryKeys.tripCities(tripId) });
      await queryClient.cancelQueries({ queryKey: queryKeys.tripCity(tripId, cityId) });

      const tripKey = queryKeys.trip(tripId);
      const cityListKey = queryKeys.tripCities(tripId);
      const cityDetailKey = queryKeys.tripCity(tripId, cityId);

      const previousTrip = queryClient.getQueryData<TripDetail>(tripKey);
      const previousCityList = queryClient.getQueryData<TripDetailCity[]>(cityListKey);
      const previousCityDetail = queryClient.getQueryData<TripDetailCity>(cityDetailKey);

      if (previousTrip) {
        const updatedTrip: TripDetail = {
          ...previousTrip,
          cities: previousTrip.cities?.map((city) => {
            if (city.id !== cityId) {
              return city;
            }

            return {
              ...city,
              transportations: city.transportations.map((transportation) =>
                transportation.id === transportationId
                  ? {
                      ...transportation,
                      isBooked,
                      amount: typeof amount === 'number' ? amount : transportation.amount,
                    }
                  : transportation
              ),
            };
          }),
        };
        queryClient.setQueryData(tripKey, updatedTrip);
      }

      if (previousCityList) {
        const updatedCities = previousCityList.map((city) => {
          if (city.id !== cityId) {
            return city;
          }
          return {
            ...city,
            transportations: city.transportations.map((transportation) =>
              transportation.id === transportationId
                ? {
                    ...transportation,
                    isBooked,
                    amount: typeof amount === 'number' ? amount : transportation.amount,
                  }
                : transportation
            ),
          };
        });
        queryClient.setQueryData(cityListKey, updatedCities);
      }

      if (previousCityDetail) {
        const updatedCityDetail: TripDetailCity = {
          ...previousCityDetail,
          transportations: previousCityDetail.transportations.map((transportation) =>
            transportation.id === transportationId
              ? {
                  ...transportation,
                  isBooked,
                  amount: typeof amount === 'number' ? amount : transportation.amount,
                }
              : transportation
          ),
        };
        queryClient.setQueryData(cityDetailKey, updatedCityDetail);
      }

      return { previousTrip, previousCityList, previousCityDetail };
    },
    onError: (_error, { tripId, cityId }, context) => {
      if (!context) {
        return;
      }

      const tripKey = queryKeys.trip(tripId);
      const cityListKey = queryKeys.tripCities(tripId);
      const cityDetailKey = queryKeys.tripCity(tripId, cityId);

      if (context.previousTrip) {
        queryClient.setQueryData(tripKey, context.previousTrip);
      }

      if (context.previousCityList) {
        queryClient.setQueryData(cityListKey, context.previousCityList);
      }

      if (context.previousCityDetail) {
        queryClient.setQueryData(cityDetailKey, context.previousCityDetail);
      }
    },
    onSettled: (_result, _error, { tripId, cityId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.trip(tripId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.tripCities(tripId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.tripCity(tripId, cityId) });
    },
  });
}
